/**
 * Pure helpers for tenant-scoped reference persist + hydrate epoch guards.
 * Kept free of Zustand/IDB so unit tests can run under node --test.
 */

/**
 * Legacy unscoped caches (no stamp) and cross-tenant stamps must not be applied.
 * @param {string|null|undefined} persistedTenantId
 * @param {string|null|undefined} selectedTenantId
 */
export function isPersistedTenantMatch(persistedTenantId, selectedTenantId) {
    if (!selectedTenantId || !persistedTenantId) return false;
    return String(persistedTenantId) === String(selectedTenantId);
}

/**
 * Merge IDB/local persisted reference slice into current state, discarding on tenant mismatch.
 *
 * @param {object|null|undefined} persistedState
 * @param {object} currentState
 * @param {string|null|undefined} selectedTenantId
 * @param {() => Record<string, unknown[]>} buildEmptyEntities
 */
export function mergeReferencePersistState(
    persistedState,
    currentState,
    selectedTenantId,
    buildEmptyEntities,
) {
    const persisted = persistedState || {};
    if (!isPersistedTenantMatch(persisted.persistedTenantId, selectedTenantId)) {
        return {
            ...currentState,
            entities: buildEmptyEntities(),
            versions: {},
            hydratedEntities: {},
            seedData: {},
            lastHydratedAt: null,
            persistedTenantId: null,
        };
    }

    return {
        ...currentState,
        ...persisted,
        entities: {
            ...buildEmptyEntities(),
            ...(persisted.entities || {}),
        },
        versions: persisted.versions || currentState.versions,
        hydratedEntities: persisted.hydratedEntities || currentState.hydratedEntities,
        seedData: persisted.seedData || currentState.seedData,
        persistedTenantId: persisted.persistedTenantId,
    };
}

/**
 * Whether a completed hydrate/refresh result may still be applied.
 * @param {number} startedEpoch
 * @param {number} currentEpoch
 */
export function shouldApplyHydrateResult(startedEpoch, currentEpoch) {
    return startedEpoch === currentEpoch;
}

/**
 * Cross-tab reference BroadcastChannel payloads must carry a tenant stamp.
 * Untagged (legacy) messages are rejected to avoid cross-tenant contamination.
 *
 * @param {string|null|undefined} messageTenantId
 * @param {string|null|undefined} selectedTenantId
 */
export function shouldAcceptReferenceBroadcast(messageTenantId, selectedTenantId) {
    return isPersistedTenantMatch(messageTenantId, selectedTenantId);
}
